import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, Modal, Table, Dropdown, Pagination } from 'react-bootstrap';
import axios from 'axios';

interface Task {
  _id: string;
  title: string;
  description: string;
  category: string;
  rewardAmount: number;
  requirements: string[];
  instructions: string;
  status: 'active' | 'inactive' | 'completed' | 'expired';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  maxSubmissions: number;
  currentSubmissions: number;
  estimatedTimeMinutes: number;
  expiryDate: string;
  allowedUserTypes: string[];
  tenureRequirements: {
    minAccountAgeDays: number;
    maxAccountAgeDays: number | null;
    requirementType: 'new_users' | 'established_users' | 'veteran_users' | 'all_users' | 'custom';
  };
  views: number;
  completionRate: number;
  createdBy: {
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface TaskFormData {
  title: string;
  description: string;
  category: string;
  rewardAmount: number;
  requirements: string[];
  instructions: string;
  priority: string;
  maxSubmissions: number;
  estimatedTimeMinutes: number;
  expiryDate: string;
  allowedUserTypes: string[];
  tenureRequirements: {
    minAccountAgeDays: number;
    maxAccountAgeDays: number | null;
    requirementType: 'new_users' | 'established_users' | 'veteran_users' | 'all_users' | 'custom';
  };
}

const TaskManagement: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Modal-specific error and success states
  const [modalError, setModalError] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginatedTasks, setPaginatedTasks] = useState<Task[]>([]);
  // Filtered tasks
  const filteredTasks = tasks.filter(task => {
    const statusMatch = statusFilter === 'all' || task.status === statusFilter;
    const categoryMatch = categoryFilter === 'all' || task.category === categoryFilter;
    return statusMatch && categoryMatch;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, categoryFilter, tasks.length]);

  useEffect(() => {
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    setPaginatedTasks(filteredTasks.slice(startIdx, endIdx));
  }, [filteredTasks, currentPage, pageSize]);

  // Form data
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    category: 'general',
    rewardAmount: 0,
    requirements: [''],
    instructions: '',
    priority: 'medium',
    maxSubmissions: 100,
    estimatedTimeMinutes: 30,
    expiryDate: '',
    allowedUserTypes: ['free', 'executive'],
    tenureRequirements: {
      minAccountAgeDays: 0,
      maxAccountAgeDays: null,
      requirementType: 'all_users'
    }
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const categories = [
    { value: 'general', label: 'General Tasks' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'survey', label: 'Survey' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'content_creation', label: 'Content Creation' },
    { value: 'data_entry', label: 'Data Entry' },
    { value: 'testing', label: 'Testing' },
    { value: 'review', label: 'Review' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'secondary' },
    { value: 'medium', label: 'Medium', color: 'primary' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'danger' }
  ];

  const tenureTypes = [
    { value: 'all_users', label: 'All Users', description: 'Available to all users regardless of account age' },
    { value: 'new_users', label: 'New Users', description: 'Users with accounts 15 days or less' },
    { value: 'established_users', label: 'Established Users', description: 'Users with accounts 15-90 days old' },
    { value: 'veteran_users', label: 'Veteran Users', description: 'Users with accounts older than 90 days' },
    { value: 'custom', label: 'Custom Range', description: 'Set custom minimum and maximum account age' }
  ];

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/tasks?limit=100&sortBy=createdAt&sortOrder=desc`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      // Sort tasks by creation date (newest first) to ensure proper ordering
      const sortedTasks = (response.data.tasks || []).sort((a: Task, b: Task) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setTasks(sortedTasks);
    } catch (error: any) {
      setError('Failed to load tasks');
      console.error('Load tasks error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'rewardAmount' || name === 'maxSubmissions' || name === 'estimatedTimeMinutes') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleRequirementChange = (index: number, value: string) => {
    const newRequirements = [...formData.requirements];
    newRequirements[index] = value;
    setFormData(prev => ({
      ...prev,
      requirements: newRequirements
    }));
  };

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  };

  const removeRequirement = (index: number) => {
    if (formData.requirements.length > 1) {
      setFormData(prev => ({
        ...prev,
        requirements: prev.requirements.filter((_, i) => i !== index)
      }));
    }
  };

  const handleUserTypeChange = (userType: string) => {
    const newUserTypes = formData.allowedUserTypes.includes(userType)
      ? formData.allowedUserTypes.filter(type => type !== userType)
      : [...formData.allowedUserTypes, userType];
    
    setFormData(prev => ({
      ...prev,
      allowedUserTypes: newUserTypes
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'general',
      rewardAmount: 0,
      requirements: [''],
      instructions: '',
      priority: 'medium',
      maxSubmissions: 100,
      estimatedTimeMinutes: 30,
      expiryDate: '',
      allowedUserTypes: ['free', 'executive'],
      tenureRequirements: {
        minAccountAgeDays: 0,
        maxAccountAgeDays: null,
        requirementType: 'all_users'
      }
    });
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);
    setModalMessage('');
    setModalError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/tasks`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.task) {
        setMessage('Task created successfully!');
        setShowAddModal(false);
        resetForm();
        loadTasks();
      }
    } catch (error: any) {
      setModalError(error.response?.data?.message || 'Failed to create task');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask) return;

    setSubmitLoading(true);
    setModalMessage('');
    setModalError('');

    try {
      const response = await axios.put(`${API_BASE_URL}/tasks/${editingTask._id}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.task) {
        setMessage('Task updated successfully!');
        setShowEditModal(false);
        setEditingTask(null);
        resetForm();
        loadTasks();
      }
    } catch (error: any) {
      setModalError(error.response?.data?.message || 'Failed to update task');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/tasks/${taskId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setMessage('Task deleted successfully!');
      loadTasks();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete task');
    }
  };

  const openEditModal = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description,
      category: task.category,
      rewardAmount: task.rewardAmount,
      requirements: task.requirements,
      instructions: task.instructions,
      priority: task.priority,
      maxSubmissions: task.maxSubmissions,
      estimatedTimeMinutes: task.estimatedTimeMinutes,
      expiryDate: task.expiryDate.split('T')[0], // Convert to date input format
      allowedUserTypes: task.allowedUserTypes,
      tenureRequirements: task.tenureRequirements || {
        minAccountAgeDays: 0,
        maxAccountAgeDays: null,
        requirementType: 'all_users'
      }
    });
    setModalError('');
    setModalMessage('');
    setShowEditModal(true);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { variant: 'success', label: 'Active' },
      inactive: { variant: 'secondary', label: 'Inactive' },
      completed: { variant: 'primary', label: 'Completed' },
      expired: { variant: 'danger', label: 'Expired' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const config = priorities.find(p => p.value === priority) || priorities[1];
    return <Badge bg={config.color}>{config.label}</Badge>;
  };


  // Set default expiry date to 7 days from now
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1><i className="fas fa-tasks me-2"></i>Task Management</h1>
              <p className="text-muted">Create and manage tasks for users to complete</p>
            </div>
            <Button variant="primary" onClick={() => {
              resetForm();
              setFormData(prev => ({ ...prev, expiryDate: getDefaultExpiryDate() }));
              setModalError('');
              setModalMessage('');
              setShowAddModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>
              Create New Task
            </Button>
          </div>
        </Col>
      </Row>

      {message && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" dismissible onClose={() => setMessage('')}>
              <i className="fas fa-check-circle me-2"></i>
              {message}
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Category</Form.Label>
                <Form.Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={() => {
                setStatusFilter('all');
                setCategoryFilter('all');
              }}>
                <i className="fas fa-refresh me-2"></i>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tasks Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading tasks...</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Task Details</th>
                    <th>Category</th>
                    <th>Reward</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Submissions</th>
                    <th>Expiry Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTasks.map(task => (
                    <tr key={task._id}>
                      <td>
                        <div>
                          <strong>{task.title}</strong>
                          <div className="text-muted small">
                            {task.description.substring(0, 100)}...
                          </div>
                          <div className="text-muted small">
                            <i className="fas fa-clock me-1"></i>
                            {task.estimatedTimeMinutes} min
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg="info">
                          {categories.find(c => c.value === task.category)?.label || task.category}
                        </Badge>
                      </td>
                      <td>
                        <strong>₹{task.rewardAmount}</strong>
                      </td>
                      <td>
                        {getStatusBadge(task.status)}
                      </td>
                      <td>
                        {getPriorityBadge(task.priority)}
                      </td>
                      <td>
                        <div className="text-center">
                          <div><strong>{task.currentSubmissions}</strong></div>
                          <div className="small text-muted">of {task.maxSubmissions}</div>
                        </div>
                      </td>
                      <td>
                        <small>
                          {new Date(task.expiryDate).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="fas fa-ellipsis-v"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => openEditModal(task)}>
                              <i className="fas fa-edit me-2"></i>Edit
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleDeleteTask(task._id)} className="text-danger">
                              <i className="fas fa-trash me-2"></i>Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination Controls (below table) */}
              <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                <div>
                  <Form.Select
                    style={{ width: 120, display: 'inline-block' }}
                    value={pageSize}
                    onChange={e => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    size="sm"
                  >
                    {[5, 10, 20, 50, 100].map(size => (
                      <option key={size} value={size}>{size} / page</option>
                    ))}
                  </Form.Select>
                </div>
                <div>
                  <Pagination className="mb-0">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                    {(() => {
                      const totalPages = Math.ceil(filteredTasks.length / pageSize);
                      let start = Math.max(1, currentPage - 2);
                      let end = Math.min(totalPages, currentPage + 2);
                      if (end - start < 4) {
                        if (start === 1) end = Math.min(totalPages, start + 4);
                        if (end === totalPages) start = Math.max(1, end - 4);
                      }
                      return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ));
                    })()}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredTasks.length / pageSize), p + 1))} disabled={currentPage === Math.ceil(filteredTasks.length / pageSize) || filteredTasks.length === 0} />
                    <Pagination.Last onClick={() => setCurrentPage(Math.ceil(filteredTasks.length / pageSize))} disabled={currentPage === Math.ceil(filteredTasks.length / pageSize) || filteredTasks.length === 0} />
                  </Pagination>
                </div>
              </div>
            </>
          )}

          {!loading && filteredTasks.length === 0 && (
            <div className="text-center p-4">
              <i className="fas fa-tasks text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="text-muted mt-3">No tasks found</h5>
              <p className="text-muted">
                {statusFilter !== 'all' || categoryFilter !== 'all' 
                  ? 'Try adjusting your filters or create a new task.'
                  : 'Create your first task to get started.'
                }
              </p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Task Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddTask}>
          <Modal.Body>
            {/* Error Alert Inside Modal */}
            {modalError && (
              <Alert variant="danger" dismissible onClose={() => setModalError('')} className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {modalError}
              </Alert>
            )}
            
            {/* Success Alert Inside Modal */}
            {modalMessage && (
              <Alert variant="success" dismissible onClose={() => setModalMessage('')} className="mb-3">
                <i className="fas fa-check-circle me-2"></i>
                {modalMessage}
              </Alert>
            )}
            
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Task Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter task title"
                    required
                    maxLength={200}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what users need to do..."
                required
                maxLength={2000}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Instructions *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                placeholder="Step-by-step instructions..."
                required
                maxLength={1000}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Requirements</Form.Label>
              {formData.requirements.map((req, index) => (
                <div key={index} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={req}
                    onChange={(e) => handleRequirementChange(index, e.target.value)}
                    placeholder={`Requirement ${index + 1}`}
                    maxLength={500}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => removeRequirement(index)}
                    disabled={formData.requirements.length === 1}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={addRequirement}>
                <i className="fas fa-plus me-2"></i>Add Requirement
              </Button>
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Reward Amount (₹) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="rewardAmount"
                    value={formData.rewardAmount}
                    onChange={handleInputChange}
                    min="0.01"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Submissions *</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxSubmissions"
                    value={formData.maxSubmissions}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Time (min) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="estimatedTimeMinutes"
                    value={formData.estimatedTimeMinutes}
                    onChange={handleInputChange}
                    min="1"
                    max="480"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>{priority.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Expiry Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* User Tenure Requirements */}
            <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Header>
                <h6 className="mb-0">
                  <i className="fas fa-users-clock me-2"></i>
                  User Tenure Requirements
                </h6>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Target User Group</Form.Label>
                  <Form.Select
                    name="tenureRequirementType"
                    value={formData.tenureRequirements.requirementType}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tenureRequirements: {
                        ...prev.tenureRequirements,
                        requirementType: e.target.value as any
                      }
                    }))}
                  >
                    {tenureTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {tenureTypes.find(t => t.value === formData.tenureRequirements.requirementType)?.description}
                  </Form.Text>
                </Form.Group>

                {formData.tenureRequirements.requirementType !== 'all_users' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Minimum Account Age (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="365"
                          value={formData.tenureRequirements.minAccountAgeDays}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            tenureRequirements: {
                              ...prev.tenureRequirements,
                              minAccountAgeDays: parseInt(e.target.value) || 0
                            }
                          }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Maximum Account Age (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="365"
                          value={formData.tenureRequirements.maxAccountAgeDays || ''}
                          placeholder="No limit"
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            tenureRequirements: {
                              ...prev.tenureRequirements,
                              maxAccountAgeDays: e.target.value ? parseInt(e.target.value) : null
                            }
                          }))}
                        />
                        <Form.Text className="text-muted">
                          Leave empty for no maximum limit
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            <Form.Group className="mb-3">
              <Form.Label>Allowed User Types</Form.Label>
              <div>
                {['free', 'executive', 'admin'].map(userType => (
                  <Form.Check
                    key={userType}
                    inline
                    type="checkbox"
                    id={`userType-${userType}`}
                    label={userType.charAt(0).toUpperCase() + userType.slice(1)}
                    checked={formData.allowedUserTypes.includes(userType)}
                    onChange={() => handleUserTypeChange(userType)}
                  />
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitLoading}>
              {submitLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-plus me-2"></i>
                  Create Task
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Task Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditTask}>
          <Modal.Body>
            {/* Error Alert Inside Modal */}
            {modalError && (
              <Alert variant="danger" dismissible onClose={() => setModalError('')} className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {modalError}
              </Alert>
            )}
            
            {/* Success Alert Inside Modal */}
            {modalMessage && (
              <Alert variant="success" dismissible onClose={() => setModalMessage('')} className="mb-3">
                <i className="fas fa-check-circle me-2"></i>
                {modalMessage}
              </Alert>
            )}
            
            {/* Same form fields as Add Modal */}
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Task Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter task title"
                    required
                    maxLength={200}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what users need to do..."
                required
                maxLength={2000}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Instructions *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                placeholder="Step-by-step instructions..."
                required
                maxLength={1000}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Requirements</Form.Label>
              {formData.requirements.map((req, index) => (
                <div key={index} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={req}
                    onChange={(e) => handleRequirementChange(index, e.target.value)}
                    placeholder={`Requirement ${index + 1}`}
                    maxLength={500}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => removeRequirement(index)}
                    disabled={formData.requirements.length === 1}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={addRequirement}>
                <i className="fas fa-plus me-2"></i>Add Requirement
              </Button>
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Reward Amount (₹) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="rewardAmount"
                    value={formData.rewardAmount}
                    onChange={handleInputChange}
                    min="0.01"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Submissions *</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxSubmissions"
                    value={formData.maxSubmissions}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Time (min) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="estimatedTimeMinutes"
                    value={formData.estimatedTimeMinutes}
                    onChange={handleInputChange}
                    min="1"
                    max="480"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>{priority.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Expiry Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Allowed User Types</Form.Label>
              <div>
                {['free', 'executive', 'admin'].map(userType => (
                  <Form.Check
                    key={userType}
                    inline
                    type="checkbox"
                    id={`edit-userType-${userType}`}
                    label={userType.charAt(0).toUpperCase() + userType.slice(1)}
                    checked={formData.allowedUserTypes.includes(userType)}
                    onChange={() => handleUserTypeChange(userType)}
                  />
                ))}
              </div>
            </Form.Group>

            {/* Tenure Requirements Section */}
            <Card className="mb-3 border-light">
              <Card.Header as="h6" className="bg-light text-muted">
                <i className="fas fa-clock me-2"></i>
                User Tenure Requirements
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Target User Group</Form.Label>
                  <Form.Select
                    name="tenureRequirements.requirementType"
                    value={formData.tenureRequirements?.requirementType || 'all_users'}
                    onChange={handleInputChange}
                  >
                    <option value="all_users">All Users (No restrictions)</option>
                    <option value="new_users">New Users (0-14 days)</option>
                    <option value="established_users">Established Users (15-29 days)</option>
                    <option value="veteran_users">Veteran Users (30+ days)</option>
                    <option value="custom">Custom Age Range</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.tenureRequirements?.requirementType === 'new_users' && 'Tasks for users who just joined the platform'}
                    {formData.tenureRequirements?.requirementType === 'established_users' && 'Tasks for users who have been active for 2-4 weeks'}
                    {formData.tenureRequirements?.requirementType === 'veteran_users' && 'Tasks for experienced platform users'}
                    {formData.tenureRequirements?.requirementType === 'custom' && 'Set your own minimum and maximum account age'}
                    {formData.tenureRequirements?.requirementType === 'all_users' && 'Task available to all users regardless of account age'}
                  </Form.Text>
                </Form.Group>

                {formData.tenureRequirements?.requirementType === 'custom' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Minimum Account Age (days)</Form.Label>
                        <Form.Control
                          type="number"
                          name="tenureRequirements.minAccountAgeDays"
                          value={formData.tenureRequirements?.minAccountAgeDays || ''}
                          onChange={handleInputChange}
                          min="0"
                          placeholder="e.g., 15"
                        />
                        <Form.Text className="text-muted">
                          Users must have accounts older than this many days
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Maximum Account Age (days)</Form.Label>
                        <Form.Control
                          type="number"
                          name="tenureRequirements.maxAccountAgeDays"
                          value={formData.tenureRequirements?.maxAccountAgeDays || ''}
                          onChange={handleInputChange}
                          min="0"
                          placeholder="e.g., 90 (optional)"
                        />
                        <Form.Text className="text-muted">
                          Optional: Exclude users with accounts older than this
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitLoading}>
              {submitLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Update Task
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TaskManagement;